// Maps a brand's lean theme tokens onto the portal's --portal-* CSS
// custom properties as INLINE styles on <html> — inline wins over both
// the base stylesheet block and the prefers-color-scheme media query, so
// the existing light/dark cascade keeps working underneath.
//
// FORMAT GOTCHA: most --portal-* color vars are space-separated "R G B"
// triplets consumed via `rgb(var(...) / alpha)` in tailwind.config; the
// gradient (--portal-body-bg, --portal-panel-bg) and --portal-button-ink
// vars are full CSS values. Emitting the wrong format silently breaks
// Tailwind alpha utilities.
//
// Derivation rules are shared verbatim with admin/iOS/Android — see the
// theming plan table: mix(a,b,t) = per-channel sRGB lerp; ink-faint =
// mix(textSecondary, backgroundBottom, .7); hairline = mix(textPrimary,
// backgroundBottom, .14); button ink = lum(accent) < 0.5 ? white : ink.

import type { EventThemeTokens, ThemeModeTokens } from "../api/types";
import { useEffect } from "react";

type Mode = "light" | "dark";

// The portal's stock palette (index.css) — fills tokens a brand leaves
// unset so derived values stay coherent.
const PORTAL_DEFAULTS: Record<Mode, Required<ThemeModeTokens>> = {
  light: {
    accent: "#AB8126",
    backgroundTop: "#F7F3EA",
    backgroundBottom: "#E8E2D7",
    surface: "#FFFFFF",
    textPrimary: "#12161E",
    textSecondary: "#4E5765",
  },
  dark: {
    accent: "#D5B56F",
    backgroundTop: "#070911",
    backgroundBottom: "#272B35",
    surface: "#0F131E",
    textPrimary: "#FBFAF7",
    textSecondary: "#C2C7D1",
  },
};

const THEMED_VARS = [
  "--portal-paper",
  "--portal-card",
  "--portal-ink",
  "--portal-ink-soft",
  "--portal-ink-faint",
  "--portal-champagne",
  "--portal-titanium",
  "--portal-hairline",
  "--portal-body-bg",
  "--portal-panel-bg",
  "--portal-button-ink",
] as const;

// ---- color math ----

function hexToRgb(hex: string): [number, number, number] {
  return [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16),
  ];
}

function triplet(hex: string): string {
  const [r, g, b] = hexToRgb(hex);
  return `${r} ${g} ${b}`;
}

function mix(a: string, b: string, t: number): string {
  const ca = hexToRgb(a);
  const cb = hexToRgb(b);
  const c = (i: number) =>
    Math.max(0, Math.min(255, Math.round(ca[i] * t + cb[i] * (1 - t))))
      .toString(16)
      .padStart(2, "0");
  return `#${c(0)}${c(1)}${c(2)}`;
}

function relativeLuminance(hex: string): number {
  const [r, g, b] = hexToRgb(hex).map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

// ---- mode resolution + application ----

function effectiveMode(): Mode {
  const forced = document.documentElement.dataset.theme;
  if (forced === "light" || forced === "dark") return forced;
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

let activeTokens: EventThemeTokens | null = null;
let watching = false;

function applyVars() {
  const root = document.documentElement;
  if (!activeTokens) {
    for (const v of THEMED_VARS) root.style.removeProperty(v);
    return;
  }
  const mode = effectiveMode();
  const t = { ...PORTAL_DEFAULTS[mode], ...(activeTokens[mode] ?? {}) };

  const inkFaint = mix(t.textSecondary, t.backgroundBottom, 0.7);
  const hairline = mix(t.textPrimary, t.backgroundBottom, 0.14);
  const accentRgb = triplet(t.accent);
  const surfaceRgb = triplet(t.surface);
  const buttonInk = relativeLuminance(t.accent) < 0.5 ? "#FBFAF7" : "#0B0A24";

  root.style.setProperty("--portal-paper", triplet(t.backgroundBottom));
  root.style.setProperty("--portal-card", surfaceRgb);
  root.style.setProperty("--portal-ink", triplet(t.textPrimary));
  root.style.setProperty("--portal-ink-soft", triplet(t.textSecondary));
  root.style.setProperty("--portal-ink-faint", triplet(inkFaint));
  root.style.setProperty("--portal-champagne", triplet(t.accent));
  root.style.setProperty("--portal-titanium", triplet(t.textSecondary));
  root.style.setProperty("--portal-hairline", triplet(hairline));
  // Same two-layer structure as the stock backdrop: a soft accent wash
  // fading out over the brand's background gradient.
  root.style.setProperty(
    "--portal-body-bg",
    `linear-gradient(180deg, rgb(${accentRgb} / 0.10) 0%, rgb(${accentRgb} / 0) 38%), ` +
      `linear-gradient(145deg, ${t.backgroundTop} 0%, ${t.backgroundBottom} 100%)`,
  );
  root.style.setProperty(
    "--portal-panel-bg",
    `linear-gradient(135deg, rgb(${surfaceRgb} / 0.94), rgb(${surfaceRgb} / 0.82) 42%, rgb(${accentRgb} / 0.13)), ` +
      `rgb(${surfaceRgb} / 0.86)`,
  );
  root.style.setProperty("--portal-button-ink", buttonInk);
}

// Re-resolve on system scheme changes AND when the footer switcher writes
// data-theme — a MutationObserver keeps this decoupled from the footer's
// local state.
function ensureWatchers() {
  if (watching) return;
  watching = true;
  window
    .matchMedia("(prefers-color-scheme: dark)")
    .addEventListener("change", applyVars);
  new MutationObserver(applyVars).observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["data-theme"],
  });
}

export function applyBrandTheme(tokens: EventThemeTokens | null | undefined) {
  activeTokens = tokens && (tokens.light || tokens.dark) ? tokens : null;
  ensureWatchers();
  applyVars();
}

export function clearBrandTheme() {
  activeTokens = null;
  applyVars();
}

// Apply while mounted, restore the stock palette on unmount (covers
// route exits and sign-out, since the themed routes unmount).
export function useBrandTheme(tokens: EventThemeTokens | null | undefined) {
  useEffect(() => {
    applyBrandTheme(tokens);
    return () => clearBrandTheme();
  }, [tokens]);
}
